import { useCallback } from 'react';

import { useOneClickAddToBag } from '../../../../hooks/useOneClickAddToBag';

import type { GetSharedLineItemProductForRestaurantQuery } from '../../GraphQL/ShareLineItemScreen.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useShareLineItemQuickAddToBag = (params: Params) => {
  const {
    deliveryDetails,
    lineItemQuantity,
    productForRestaurant,
    navigateToLocationSearch,
    onAddProductToBagSuccess,
    sharedLineItemSlug,
  } = params;

  // ─────────────────────────────────────────────────────────────────────

  const { addProductToBag, isAddingProductToBag } = useOneClickAddToBag({
    restaurantSlug: productForRestaurant?.restaurant.slug ?? '',
    deliveryDetails,
    onSuccess: onAddProductToBagSuccess,
    sharedLineItemSlug,
  });

  // ─── Handlers ────────────────────────────────────────────────────────

  const quickAddToBag = useCallback(() => {
    if (!productForRestaurant) {
      navigateToLocationSearch();

      return;
    }

    void addProductToBag(productForRestaurant, lineItemQuantity);
  }, [addProductToBag, lineItemQuantity, navigateToLocationSearch, productForRestaurant]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isAddingProductToBag,
    quickAddToBag,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  deliveryDetails?: DeliveryDetails;
  lineItemQuantity: number;
  navigateToLocationSearch: () => void;
  onAddProductToBagSuccess: () => void;
  productForRestaurant?: ProductForRestaurant;
  sharedLineItemSlug: string;
};

type DeliveryDetails = {
  addressId: string;
  addressName: string | null;
  completeAddress: string;
  vendorRestaurantId: string;
  vendor: string;
  deliveryFee: number;
  deliveryEstimate?: string | null;
  tip: number;
};

type ProductForRestaurant = NonNullable<
  GetSharedLineItemProductForRestaurantQuery['productForRestaurant']
>;
